import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, CheckCircle2, Circle, Menu, Lock, Compass, Award, RefreshCw } from "lucide-react";
import { PortalHeader, useLearner } from "./PortalDashboard";
import { previewStore } from "./previewStore";
import PreviewBanner from "./PreviewBanner";
import api from "../../lib/api";
import usePageMeta from "../../lib/usePageMeta";

const COURSE_CODE = "HOSP-101";

// ---------------------------------------------------------------------------
// HOSP-101 — Practicing From Diagnosis to Goodbye
// Session 1 is fully populated. Sessions 2–6 render as locked placeholders in
// the sidebar. Access to this page requires an active admin preview session
// (previewStore) — the /admin/portal/preview/course endpoint enforces admin
// authentication server-side, so a non-admin flipping sessionStorage cannot
// load course content.
// ---------------------------------------------------------------------------

export default function PortalHosp101Learn() {
  usePageMeta({ title: "HOSP-101 — Session 1 · ENTER | CCCHAPS Academy Preview", noindex: true });
  const inPreview = previewStore.isActive() && previewStore.getCourseCode() === "HOSP-101";
  const learner = useLearner();
  const nav = useNavigate();
  const [course, setCourse] = useState(null);
  const [pos, setPos] = useState(0);
  const [navOpen, setNavOpen] = useState(false);
  const [answers, setAnswers] = useState({});   // { [screenId]: { key, correct } }
  const [assessmentAnswers, setAssessmentAnswers] = useState({}); // { [questionId]: key } — no client-side correct
  const [assessmentResult, setAssessmentResult] = useState(null); // { score, total, percent, passed, review, ... }
  const [assessmentSubmitting, setAssessmentSubmitting] = useState(false);
  const [reflectionAcked, setReflectionAcked] = useState(false);
  const [completionState, setCompletionState] = useState(null); // server truth
  const [activeSessionOrder, setActiveSessionOrder] = useState(1);

  const refreshCompletionState = async () => {
    try {
      const r = await api.get("/admin/portal/preview/hosp101/completion-state");
      setCompletionState(r.data);
      if (r.data?.reflection?.acknowledged) setReflectionAcked(true);
    } catch (_e) { /* silent — endpoint requires admin */ }
  };

  useEffect(() => {
    if (!inPreview) return;
    (async () => {
      try {
        const r = await api.get("/admin/portal/preview/course", { params: { code: COURSE_CODE } });
        setCourse(r.data.course);
        refreshCompletionState();
      } catch (err) {
        toast.error("Preview mode requires admin sign-in.");
        previewStore.exit();
        nav("/care-portal/admin/academy-portal");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inPreview, nav]);

  // Guard: if this page is opened without admin preview, redirect back.
  if (!inPreview) {
    return (
      <div className="min-h-screen bg-[#FDFCF6] flex items-center justify-center px-4">
        <div className="max-w-md text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-slate-100 text-slate-500 mb-4">
            <Lock className="w-5 h-5" />
          </div>
          <h1 className="font-heading text-2xl text-slate-900">HOSP-101 is in development</h1>
          <p className="mt-3 text-sm text-slate-600">This course is available for admin preview only. It is not yet open for public enrollment.</p>
          <Link to="/academy" className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-[#4A6FA5] hover:underline">
            Return to Academy <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen bg-[#FDFCF6]">
        <PortalHeader learner={learner} />
        <div className="p-12 text-center text-slate-500">Loading HOSP-101 preview…</div>
      </div>
    );
  }

  const sessions = course.sessions || [];
  const availableSessions = sessions.filter(s => s.status === "available");

  const activeSession = sessions.find(s => s.order === activeSessionOrder) || sessions[0];
  const screens = activeSession?.screens || [];

  // Steps within the current session: [overview, ...screens]
  const totalSteps = 1 + screens.length;
  const currentIsOverview = pos === 0;
  const currentScreen = pos > 0 ? screens[pos - 1] : null;

  // Screen-level progress persistence via previewStore (client-only, sidebar UX)
  const previewState = previewStore.getState();
  const completions = previewState.progress?.section_completions || {};
  const screenDone = (id) => !!completions[`hosp101::${id}`];
  const markScreenDone = (id) => {
    previewStore.markSectionComplete(`hosp101::${id}`);
    // Server-side persistence for Sessions 1-6 instructional screens only.
    // The server enforces validity so unknown ids or non-S1-6 orders are rejected.
    if (activeSessionOrder >= 1 && activeSessionOrder <= 6 && id) {
      api.post("/admin/portal/preview/hosp101/progress/screen-complete", {
        session_order: activeSessionOrder,
        screen_id: id,
      }).then(() => refreshCompletionState()).catch(() => { /* silent; nonblocking */ });
    }
  };

  const currentAnswered = (() => {
    if (!currentScreen) return true;
    if (currentScreen.kind === "knowledge_check") return !!answers[currentScreen.id];
    if (currentScreen.kind === "assessment_question") return !!assessmentAnswers[currentScreen.id];
    if (currentScreen.kind === "assessment_gate") {
      // Must submit and pass to advance; retake stays on this screen.
      return !!(assessmentResult && assessmentResult.passed);
    }
    if (currentScreen.kind === "reflection_ack") return !!reflectionAcked;
    return true;
  })();

  const canAdvance = currentAnswered;

  const submitAssessment = async () => {
    // Guard: all 10 questions must have an answer selected.
    const questionScreens = (activeSession?.screens || []).filter(s => s.kind === "assessment_question");
    const missing = questionScreens.filter(s => !assessmentAnswers[s.id]);
    if (missing.length > 0) {
      toast.error(`Please answer all ${questionScreens.length} questions before submitting. Missing: ${missing.length}.`);
      return;
    }
    try {
      setAssessmentSubmitting(true);
      const r = await api.post("/admin/portal/preview/hosp101/assessment/submit", { answers: assessmentAnswers });
      setAssessmentResult(r.data);
      if (r.data.passed) {
        toast.success(`Passed \u2014 ${r.data.percent}% (${r.data.score}/${r.data.total}).`);
      } else {
        toast.error(`Score ${r.data.percent}% (${r.data.score}/${r.data.total}). 80% required. Retake permitted.`);
      }
      refreshCompletionState();
    } catch (e) {
      toast.error("Assessment submission failed. Please try again.");
    } finally {
      setAssessmentSubmitting(false);
    }
  };

  const retakeAssessment = () => {
    setAssessmentAnswers({});
    setAssessmentResult(null);
    // Jump back to the first assessment question screen in this session.
    const firstQ = (activeSession?.screens || []).findIndex(s => s.kind === "assessment_question");
    if (firstQ >= 0) setPos(firstQ + 1); // +1 for overview
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const acknowledgeReflection = async () => {
    try {
      await api.post("/admin/portal/preview/hosp101/reflection/acknowledge", { acknowledged: true });
      setReflectionAcked(true);
      toast.success("Final Course Reflection acknowledged.");
      refreshCompletionState();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not save acknowledgement.";
      toast.error(msg);
    }
  };

  const doneCount = screens.filter(sc => screenDone(sc.id)).length;
  const pct = screens.length ? Math.round(doneCount * 100 / screens.length) : 0;

  // Total progress across ALL available sessions (for overview display)
  const allAvailableScreens = availableSessions.flatMap(s => s.screens);
  const globalDoneCount = allAvailableScreens.filter(sc => screenDone(sc.id)).length;
  const globalPct = allAvailableScreens.length ? Math.round(globalDoneCount * 100 / allAvailableScreens.length) : 0;

  // Determine next available session (used when advancing off the last screen of a session).
  const nextAvailableSession = availableSessions.find(s => s.order > activeSessionOrder);

  const goNext = () => {
    if (currentScreen) markScreenDone(currentScreen.id);
    if (pos < totalSteps - 1) {
      setPos(p => p + 1);
    } else if (nextAvailableSession) {
      // Advance from the last screen of the current session to the first screen of the next available session.
      setActiveSessionOrder(nextAvailableSession.order);
      setPos(1);
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const goPrev = () => {
    if (pos > 0) {
      setPos(p => p - 1);
    } else {
      // At the overview of a non-first session, back-arrow returns to the last screen of the previous available session.
      const prevAvail = [...availableSessions].reverse().find(s => s.order < activeSessionOrder);
      if (prevAvail) {
        setActiveSessionOrder(prevAvail.order);
        setPos(prevAvail.screens.length);
      }
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const jumpTo = (n) => {
    setPos(n);
    setNavOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const jumpToSession = (order, screenIndex = 1) => {
    setActiveSessionOrder(order);
    setPos(screenIndex);
    setNavOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const isLastScreenOfCourse = pos === totalSteps - 1 && !nextAvailableSession;
  const isLastScreenOfSession = pos === totalSteps - 1;

  return (
    <div className="bg-[#FDFCF6] min-h-screen" data-testid="portal-hosp101-learn">
      <PreviewBanner
        course={course}
        resetTo="/academy/portal/hosp-101/learn"
        onSyncPreview={() => { setPos(0); setActiveSessionOrder(1); setAnswers({}); }}
      />
      <PortalHeader learner={learner} />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 md:py-10 grid lg:grid-cols-[280px_1fr] gap-6">
        {/* Sidebar */}
        <aside className={`${navOpen ? "block" : "hidden"} lg:block`} data-testid="hosp101-sidebar">
          <div className="bg-white border border-slate-200 rounded-2xl p-4 sticky top-24">
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#4A6FA5] font-semibold">HOSP-101</p>
            <h3 className="mt-1 font-heading text-base text-slate-900 leading-tight">{course.title}</h3>
            <p className="mt-1 text-[11px] text-slate-500">{course.subtitle}</p>
            <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
              <span>Session {activeSessionOrder}: {doneCount}/{screens.length}</span><span>{pct}%</span>
            </div>
            <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mt-1">
              <div className="h-full bg-[#4A6FA5]" style={{ width: `${pct}%` }} />
            </div>
            {availableSessions.length > 1 && (
              <p className="mt-2 text-[10px] text-slate-400">
                Course total: {globalDoneCount}/{allAvailableScreens.length} screens ({globalPct}%)
              </p>
            )}

            <ul className="mt-5 space-y-1 text-sm" data-testid="hosp101-session-list">
              <li>
                <button onClick={() => jumpTo(0)} data-testid="hosp101-overview-link"
                        className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-md text-left ${currentIsOverview ? "bg-[#F0F4F8] text-[#3B597B] font-medium" : "text-slate-700 hover:bg-slate-50"}`}>
                  <Compass className="w-3.5 h-3.5" /><span>Course Overview</span>
                </button>
              </li>
              {sessions.map((sess) => {
                const active = sess.status === "available";
                const isCurrentSession = sess.order === activeSessionOrder && !currentIsOverview;
                return (
                  <li key={sess.id}>
                    <details open={active} className="group">
                      <summary
                        onClick={(e) => {
                          if (active) {
                            // Clicking the session header jumps to its first screen.
                            e.preventDefault();
                            jumpToSession(sess.order, 1);
                          }
                        }}
                        className={`cursor-pointer list-none flex items-center gap-2 px-2.5 py-2 rounded-md ${isCurrentSession ? "bg-[#EEF3FA] text-[#3B597B] font-medium" : "text-slate-600 hover:bg-slate-50"} ${!active ? "opacity-60" : ""}`}
                        data-testid={`hosp101-session-${sess.order}`}
                      >
                        {!active && <Lock className="w-3.5 h-3.5 text-slate-400" />}
                        <span className="text-[11px] uppercase tracking-[0.14em] font-semibold">S{sess.order} · {sess.key}</span>
                      </summary>
                      {active && sess.screens.length > 0 && (
                        <ul className="mt-1 pl-2 border-l border-slate-100 space-y-0.5">
                          {sess.screens.map((sc, i) => {
                            const done = screenDone(sc.id);
                            const isCurr = isCurrentSession && pos === i + 1;
                            return (
                              <li key={sc.id}>
                                <button onClick={() => jumpToSession(sess.order, i + 1)}
                                        data-testid={`hosp101-nav-${sc.id}`}
                                        className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md text-left text-[13px] ${isCurr ? "bg-[#F0F4F8] text-[#3B597B] font-medium" : "text-slate-600 hover:bg-slate-50"}`}>
                                  {done ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> : <Circle className="w-3.5 h-3.5 text-slate-300" />}
                                  <span className="truncate">{sc.eyebrow || `Screen ${sc.order}`}</span>
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                      {!active && (
                        <p className="pl-2 mt-1 text-[11px] text-slate-400 italic">Content in development</p>
                      )}
                    </details>
                  </li>
                );
              })}
            </ul>
          </div>
        </aside>

        <main className="min-w-0">
          <div className="lg:hidden mb-4">
            <button onClick={() => setNavOpen(v => !v)} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-300 text-sm text-slate-700">
              <Menu className="w-4 h-4" /> {navOpen ? "Close" : "Sessions"}
            </button>
          </div>

          {currentIsOverview ? (
            <OverviewCard course={course} onStart={() => jumpToSession(1, 1)} />
          ) : (
            <ScreenCard
              screen={currentScreen}
              session={activeSession}
              stepLabel={`Session ${activeSessionOrder} \u00b7 ${pos} of ${screens.length}`}
              answer={answers[currentScreen.id]}
              onAnswer={(k, correct) => {
                setAnswers(a => ({ ...a, [currentScreen.id]: { key: k, correct } }));
                // Persist formative KC attempt for Sessions 1-6 (nonblocking, silent).
                if (activeSessionOrder >= 1 && activeSessionOrder <= 6 && currentScreen?.kind === "knowledge_check") {
                  api.post("/admin/portal/preview/hosp101/progress/kc-attempt", {
                    session_order: activeSessionOrder,
                    screen_id: currentScreen.id,
                    selected_key: k,
                  }).then(() => refreshCompletionState()).catch(() => { /* silent */ });
                }
              }}
              assessmentAnswer={assessmentAnswers[currentScreen.id]}
              onAssessmentAnswer={(k) => setAssessmentAnswers(a => ({ ...a, [currentScreen.id]: k }))}
              assessmentResult={assessmentResult}
              assessmentSubmitting={assessmentSubmitting}
              onSubmitAssessment={submitAssessment}
              onRetakeAssessment={retakeAssessment}
              reflectionAcked={reflectionAcked}
              onAcknowledgeReflection={acknowledgeReflection}
              completionState={completionState}
              onViewCertificate={() => nav("/academy/portal/hosp-101/certificate")}
              canGoPrev={pos > 0 || availableSessions[0]?.order !== activeSessionOrder}
              onPrev={goPrev}
              onNext={goNext}
              nextDisabled={!canAdvance}
              isLast={isLastScreenOfCourse}
              nextLabel={
                currentScreen?.kind === "course_complete"
                  ? "View Certificate of Completion"
                  : (isLastScreenOfSession && nextAvailableSession
                      ? `Continue to Session ${nextAvailableSession.order} \u2014 ${nextAvailableSession.key}`
                      : (currentScreen?.cta_label || "Continue"))
              }
              onFinish={() => nav("/care-portal/admin/academy-portal")}
            />
          )}
        </main>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// OverviewCard — course entry
// ---------------------------------------------------------------------------
function OverviewCard({ course, onStart }) {
  const s1 = (course.sessions || [])[0];
  return (
    <article className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8" data-testid="hosp101-overview">
      <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 border border-amber-200 px-2.5 py-1 text-[10px] uppercase tracking-[0.2em] font-semibold text-amber-800 mb-4">
        {course.badge}
      </span>
      <p className="text-xs uppercase tracking-[0.24em] text-[#4A6FA5] font-semibold">HOSP-101</p>
      <h1 className="mt-2 font-heading text-3xl sm:text-4xl tracking-tight text-slate-900 leading-tight">{course.title}</h1>
      <p className="mt-2 text-slate-600 italic">{course.subtitle}</p>

      <p className="mt-6 text-slate-700 leading-relaxed">{course.description}</p>

      <div className="mt-8 rounded-xl border border-slate-200 bg-[#F0F4F8] p-5">
        <p className="text-[10px] uppercase tracking-[0.22em] text-[#4A6FA5] font-semibold">Session 1 · {s1?.key}</p>
        <h2 className="mt-1 font-heading text-xl text-slate-900">{s1?.title}</h2>
        <p className="mt-2 text-sm text-slate-700 leading-relaxed">{s1?.subtitle}</p>
        {s1?.governing_principle && (
          <div className="mt-4 rounded-lg border-l-4 border-[#4A6FA5] bg-white px-4 py-3">
            <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#4A6FA5]">Governing Principle</p>
            <p className="mt-1 font-heading text-slate-900">{s1.governing_principle}</p>
          </div>
        )}
      </div>

      <div className="mt-6 flex flex-wrap gap-3">
        <button onClick={onStart} data-testid="hosp101-start-session1"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-[#4A6FA5] text-white font-medium hover:bg-[#3B597B]">
          Begin Session 1 <ArrowRight className="w-4 h-4" />
        </button>
      </div>

      <p className="mt-6 text-[11px] text-slate-500 leading-relaxed">{course.required_disclaimer}</p>

      <div className="mt-8 pt-6 border-t border-slate-100">
        <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold mb-3">Course roadmap</p>
        <ol className="grid sm:grid-cols-2 gap-2">
          {(course.sessions || []).map(s => (
            <li key={s.id} className={`rounded-lg border px-3 py-2 text-sm flex items-center justify-between gap-3 ${s.status === "available" ? "border-[#4A6FA5] bg-[#F8FAFC]" : "border-slate-200 bg-white opacity-70"}`}>
              <span>
                <span className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#4A6FA5]">S{s.order} · {s.key}</span>
                <span className="ml-2 text-slate-700">{s.title}</span>
              </span>
              {s.status === "available"
                ? <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                : <Lock className="w-3.5 h-3.5 text-slate-400 shrink-0" />}
            </li>
          ))}
        </ol>
      </div>
    </article>
  );
}

// ---------------------------------------------------------------------------
// ScreenCard \u2014 renders one screen at a time (content / KC / reflection / assessment / etc.)
// ---------------------------------------------------------------------------
function ScreenCard({
  screen, session, stepLabel,
  answer, onAnswer,
  assessmentAnswer, onAssessmentAnswer,
  assessmentResult, assessmentSubmitting, onSubmitAssessment, onRetakeAssessment,
  reflectionAcked, onAcknowledgeReflection,
  completionState, onViewCertificate,
  canGoPrev, onPrev, onNext, nextDisabled, isLast, nextLabel = "Continue", onFinish,
}) {
  // Special override: on course_complete screen, "next" navigates directly to the certificate page.
  const isCourseComplete = screen.kind === "course_complete";
  const isAssessmentGate = screen.kind === "assessment_gate";
  const isReflectionAck = screen.kind === "reflection_ack";
  const handleNext = isCourseComplete ? onViewCertificate : onNext;
  return (
    <article className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8" data-testid={`hosp101-screen-${screen.id}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-[11px] uppercase tracking-[0.22em] text-[#4A6FA5] font-semibold">{screen.eyebrow}</p>
        <p className="text-[11px] uppercase tracking-[0.16em] text-slate-400">{stepLabel}</p>
      </div>
      {screen.section && (
        <h1 className="mt-2 font-heading text-2xl sm:text-3xl tracking-tight text-slate-900 leading-tight">{screen.section}</h1>
      )}
      {screen.subtitle && <p className="mt-2 text-slate-600 italic">{screen.subtitle}</p>}

      {/* Body */}
      <div className="mt-6 space-y-5">
        {(screen.kind === "content" || screen.kind === "closing" || screen.kind === "assessment_intro" || screen.kind === "course_complete") &&
          (screen.blocks || []).map((b, i) => <Block key={i} block={b} />)
        }

        {screen.kind === "next_preview" && (
          <>
            {(screen.blocks || []).map((b, i) => <Block key={i} block={b} />)}
            {screen.dev_notice && (
              <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-600" data-testid={`hosp101-devnotice-${screen.id}`}>
                {screen.dev_notice}
              </div>
            )}
          </>
        )}

        {screen.kind === "reflection" && (
          <>
            {(screen.blocks || []).map((b, i) => <Block key={i} block={b} />)}
            <ol className="space-y-2 list-none">
              {(screen.prompts || []).map((p, i) => {
                const isObj = typeof p === "object" && p !== null;
                const title = isObj ? p.title : p;
                const items = isObj ? (p.items || []) : [];
                return (
                <li key={i} className="rounded-lg border border-slate-200 bg-[#F8FAFC] px-4 py-3" data-testid={`hosp101-reflection-${i + 1}`}>
                  <p className="text-sm text-slate-800 leading-relaxed font-medium">{title}</p>
                  {items.length > 0 && (
                    <ul className="mt-1.5 space-y-1 pl-1">
                      {items.map((it, j) => (
                        <li key={j} className="flex items-start gap-2 text-sm text-slate-700 leading-relaxed">
                          <span className="mt-2 w-1 h-1 rounded-full bg-slate-400 shrink-0" />
                          <span>{it}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  <textarea rows={2} placeholder="For your own reflection. Not stored."
                            className="mt-2 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#4A6FA5] resize-none" />
                </li>
                );
              })}
            </ol>
            {(screen.closing_blocks || []).map((b, i) => <Block key={`cb-${i}`} block={b} />)}
            {screen.note && <p className="text-[11px] text-slate-500 italic">{screen.note}</p>}
          </>
        )}

        {screen.kind === "knowledge_check" && (
          <KnowledgeCheck screen={screen} answer={answer} onAnswer={onAnswer} />
        )}

        {screen.kind === "assessment_question" && (
          <AssessmentQuestion screen={screen} answer={assessmentAnswer} onAnswer={onAssessmentAnswer} />
        )}

        {isAssessmentGate && (
          <AssessmentGate
            screen={screen}
            result={assessmentResult}
            submitting={assessmentSubmitting}
            onSubmit={onSubmitAssessment}
            onRetake={onRetakeAssessment}
          />
        )}

        {isReflectionAck && (
          <ReflectionAck
            screen={screen}
            acked={reflectionAcked}
            onAck={onAcknowledgeReflection}
          />
        )}
      </div>

      {/* Navigation */}
      <div className="mt-10 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 border-t border-slate-100 pt-6">
        <button onClick={onPrev} disabled={!canGoPrev} data-testid="hosp101-prev"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-slate-300 text-slate-700 text-sm font-medium hover:border-[#4A6FA5] disabled:opacity-40 disabled:cursor-not-allowed">
          <ArrowLeft className="w-4 h-4" /> Previous
        </button>
        {isCourseComplete ? (
          <CourseCompleteCtas
            completionState={completionState}
            onViewCertificate={onViewCertificate}
            onFinish={onFinish}
          />
        ) : isLast ? (
          <button onClick={onFinish} data-testid="hosp101-finish"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#4A6FA5] text-white text-sm font-medium hover:bg-[#3B597B]">
            Return to Admin Portal <ArrowRight className="w-4 h-4" />
          </button>
        ) : (
          <button onClick={handleNext} disabled={nextDisabled} data-testid="hosp101-next"
                  title={nextDisabled ? "Complete this step to continue." : ""}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#4A6FA5] text-white text-sm font-medium hover:bg-[#3B597B] disabled:opacity-50 disabled:cursor-not-allowed">
            {nextLabel} <ArrowRight className="w-4 h-4" />
          </button>
        )}
      </div>
    </article>
  );
}

// ---------------------------------------------------------------------------
// CourseCompleteCtas \u2014 renders the "View Certificate of Completion" CTA when
// the server confirms all four completion requirements are met, otherwise
// renders a per-requirement checklist so the learner is never given a false
// completion state. Server enforces eligibility again on the certificate route.
// ---------------------------------------------------------------------------
function CourseCompleteCtas({ completionState, onViewCertificate, onFinish }) {
  const eligible = completionState?.eligible_for_certificate === true;
  const req = completionState?.requirements || {};
  const missing = [
    { key: "sessions_1_to_6_completed", label: "Complete all six instructional sessions" },
    { key: "formative_kcs_attempted", label: "Attempt every formative knowledge check" },
    { key: "final_assessment_passed_80", label: "Pass the Final Competency Assessment (\u226580%)" },
    { key: "final_reflection_acknowledged", label: "Acknowledge the Final Course Reflection" },
  ].filter(r => !req[r.key]);
  return (
    <div className="flex flex-col items-stretch sm:items-end gap-3 w-full sm:w-auto">
      {!eligible && missing.length > 0 && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-xs text-amber-900 max-w-md" data-testid="hosp101-cert-requirements">
          <p className="font-semibold uppercase tracking-[0.14em] mb-1.5">Still needed to unlock your certificate</p>
          <ul className="space-y-1 list-disc pl-4">
            {missing.map(r => <li key={r.key}>{r.label}</li>)}
          </ul>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-3 justify-end">
        <button onClick={onFinish} data-testid="hosp101-finish"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-slate-300 bg-white text-slate-700 text-sm font-medium hover:border-[#4A6FA5]">
          Return to Admin Portal
        </button>
        <button
          onClick={onViewCertificate}
          disabled={!eligible}
          data-testid="hosp101-cert-cta"
          title={eligible ? "Open your Certificate of Completion" : "Complete all four requirements to unlock your certificate"}
          className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold ${
            eligible
              ? "bg-[#4A6FA5] text-white hover:bg-[#3B597B]"
              : "bg-slate-200 text-slate-500 cursor-not-allowed"
          }`}
        >
          <Award className="w-4 h-4" /> View Certificate of Completion <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Block renderer — one row of instructional content
// ---------------------------------------------------------------------------
function Block({ block }) {
  if (block.type === "paragraph") {
    return <p className="text-slate-700 leading-relaxed">{block.text}</p>;
  }
  if (block.type === "list") {
    return (
      <ul className="space-y-1.5">
        {block.items.map((it, i) => (
          <li key={i} className="flex items-start gap-2 text-slate-700 leading-relaxed">
            <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#4A6FA5] shrink-0" />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    );
  }
  if (block.type === "stack") {
    return (
      <div className="space-y-1">
        {block.items.map((it, i) => (
          <p key={i} className="text-slate-800 leading-snug">{it}</p>
        ))}
      </div>
    );
  }
  if (block.type === "subheading") {
    return <h3 className="font-heading text-lg text-slate-900 mt-2 tracking-tight" data-testid="hosp101-subheading">{block.text}</h3>;
  }
  if (block.type === "emphasis") {
    return <p className="pl-4 border-l-2 border-slate-300 italic text-slate-700 leading-relaxed">{block.text}</p>;
  }
  if (block.type === "quote") {
    return (
      <figure className="rounded-xl border border-slate-200 bg-[#FBF8F1] px-5 py-4 shadow-[inset_2px_0_0_0_#B8925A]">
        {block.speaker && (
          <figcaption className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#8B6B33] mb-1.5">
            {block.speaker}
          </figcaption>
        )}
        <blockquote className="font-heading text-slate-900 leading-snug">&ldquo;{block.text}&rdquo;</blockquote>
      </figure>
    );
  }
  if (block.type === "principle") {
    return (
      <div className="rounded-xl border-l-4 border-[#4A6FA5] bg-[#F0F4F8] px-5 py-4">
        {block.label && (
          <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#4A6FA5] mb-1">{block.label}</p>
        )}
        <p className="font-heading text-slate-900 leading-snug">{block.text}</p>
      </div>
    );
  }
  return null;
}

// ---------------------------------------------------------------------------
// KnowledgeCheck — accessible answer buttons with post-submit feedback
// ---------------------------------------------------------------------------
function KnowledgeCheck({ screen, answer, onAnswer }) {
  const submitted = !!answer;
  const correctKey = screen.correct;

  return (
    <div className="space-y-6" data-testid={`hosp101-kc-${screen.id}`}>
      {(screen.case || []).map((b, i) => <Block key={i} block={b} />)}

      <fieldset className="rounded-xl border border-slate-200 bg-white p-4 md:p-5">
        <legend className="px-2 font-heading text-slate-900" data-testid={`hosp101-kc-prompt-${screen.id}`}>{screen.prompt}</legend>
        <div className="mt-3 space-y-2" role="radiogroup" aria-label={screen.prompt}>
          {screen.options.map(opt => {
            const isPicked = answer?.key === opt.key;
            const isCorrect = opt.key === correctKey;
            const showState = submitted;
            const stateClass = !showState
              ? "border-slate-200 bg-white text-slate-800 hover:border-[#4A6FA5]"
              : isCorrect
                ? "border-emerald-400 bg-emerald-50 text-emerald-900"
                : isPicked
                  ? "border-amber-400 bg-amber-50 text-amber-900"
                  : "border-slate-200 bg-white text-slate-500";
            return (
              <button
                key={opt.key}
                onClick={() => !submitted && onAnswer(opt.key, isCorrect)}
                disabled={submitted}
                role="radio"
                aria-checked={isPicked}
                aria-label={`Option ${opt.key.toUpperCase()}: ${opt.label}`}
                data-testid={`hosp101-kc-opt-${screen.id}-${opt.key}`}
                className={`w-full text-left rounded-lg border px-3 py-2.5 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-[#4A6FA5] focus:ring-offset-1 ${stateClass} ${submitted ? "cursor-default" : "cursor-pointer"}`}
              >
                <span className="font-semibold mr-2">{opt.key.toUpperCase()}.</span>{opt.label}
                {submitted && isPicked && !isCorrect && (
                  <span className="ml-2 text-[11px] uppercase tracking-[0.14em] font-semibold text-amber-700">Your answer</span>
                )}
                {submitted && isCorrect && (
                  <span className="ml-2 text-[11px] uppercase tracking-[0.14em] font-semibold text-emerald-700">Correct answer</span>
                )}
              </button>
            );
          })}
        </div>
      </fieldset>

      {submitted && (
        <div className="rounded-xl border border-slate-200 bg-[#F8FAFC] p-4 md:p-5" data-testid={`hosp101-kc-feedback-${screen.id}`}>
          <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#4A6FA5] mb-2">Feedback</p>
          {(screen.feedback_paragraphs || []).map((p, i) => (
            <p key={i} className="text-sm text-slate-700 leading-relaxed mb-2 last:mb-0">{p}</p>
          ))}
          {(screen.feedback_blocks || []).map((b, i) => <Block key={`fb-${i}`} block={b} />)}
          {screen.feedback_quotes && (
            <div className="mt-3 space-y-2">
              {screen.feedback_quotes.map((q, i) => (
                <figure key={i} className="rounded-lg border border-slate-200 bg-[#FBF8F1] px-4 py-3 shadow-[inset_2px_0_0_0_#B8925A]">
                  {q.speaker && (
                    <figcaption className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#8B6B33] mb-1">
                      {q.speaker}
                    </figcaption>
                  )}
                  <blockquote className="font-heading text-slate-900 leading-snug text-sm">&ldquo;{q.text}&rdquo;</blockquote>
                </figure>
              ))}
            </div>
          )}
          {screen.feedback_middle && (
            <p className="mt-3 text-sm text-slate-700 leading-relaxed">{screen.feedback_middle}</p>
          )}
          {screen.feedback_list && (
            <ul className="mt-2 space-y-1">
              {screen.feedback_list.map((it, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                  <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#4A6FA5] shrink-0" />
                  <span>{it}</span>
                </li>
              ))}
            </ul>
          )}
          {screen.feedback_close && (
            <p className="mt-3 text-sm text-slate-800 font-medium">{screen.feedback_close}</p>
          )}
          {screen.principle && (
            <div className="mt-4 rounded-lg border-l-4 border-[#4A6FA5] bg-white px-4 py-3">
              <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#4A6FA5]">{screen.principle.label}</p>
              <p className="mt-1 font-heading text-slate-900">{screen.principle.text}</p>
            </div>
          )}
          {screen.feedback_final && (
            <p className="mt-3 text-sm text-slate-800 font-medium">{screen.feedback_final}</p>
          )}
          <p className={`mt-3 text-xs font-semibold ${answer.correct ? "text-emerald-700" : "text-slate-500"}`}>
            {answer.correct ? "You selected the strongest response." : "You may continue after reviewing the feedback."}
          </p>
        </div>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// AssessmentQuestion \u2014 like KnowledgeCheck but WITHOUT client-side correct answer.
// Feedback is only shown after the full assessment is submitted (on the gate).
// ---------------------------------------------------------------------------
function AssessmentQuestion({ screen, answer, onAnswer }) {
  return (
    <div className="space-y-5">
      {(screen.case || []).map((b, i) => <Block key={i} block={b} />)}
      <fieldset className="rounded-xl border border-slate-200 bg-white p-4 md:p-5">
        <legend className="px-2 font-heading text-slate-900" data-testid={`hosp101-fa-prompt-${screen.id}`}>{screen.prompt}</legend>
        <div className="mt-3 space-y-2" role="radiogroup" aria-label={screen.prompt}>
          {screen.options.map(opt => {
            const isPicked = answer === opt.key;
            return (
              <button
                key={opt.key}
                onClick={() => onAnswer(opt.key)}
                role="radio"
                aria-checked={isPicked}
                aria-label={`Option ${opt.key.toUpperCase()}: ${opt.label}`}
                data-testid={`hosp101-fa-opt-${screen.id}-${opt.key}`}
                className={`w-full text-left rounded-lg border px-3 py-2.5 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-[#4A6FA5] focus:ring-offset-1 cursor-pointer ${
                  isPicked
                    ? "border-[#4A6FA5] bg-[#EEF3FA] text-slate-900"
                    : "border-slate-200 bg-white text-slate-800 hover:border-[#4A6FA5]"
                }`}
              >
                <span className="font-semibold mr-2">{opt.key.toUpperCase()}.</span>{opt.label}
              </button>
            );
          })}
        </div>
      </fieldset>
      <p className="text-[11px] text-slate-500 italic">Your answer will not be scored until you submit the full assessment on the results screen.</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AssessmentGate \u2014 submit + result review.
// ---------------------------------------------------------------------------
function AssessmentGate({ screen, result, submitting, onSubmit, onRetake }) {
  return (
    <div className="space-y-5" data-testid="hosp101-fa-gate">
      {(screen.blocks || []).map((b, i) => <Block key={i} block={b} />)}
      {!result && (
        <div className="rounded-xl border border-[#4A6FA5]/40 bg-[#F8FAFC] p-5 text-center">
          <p className="font-heading text-lg text-slate-900">Ready to submit?</p>
          <p className="mt-2 text-sm text-slate-600">Once submitted, your score and per-question rationale will appear below.</p>
          <button
            onClick={onSubmit}
            disabled={submitting}
            data-testid="hosp101-fa-submit"
            className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#4A6FA5] text-white text-sm font-medium hover:bg-[#3B597B] disabled:opacity-50"
          >
            {submitting ? "Scoring\u2026" : "Submit Assessment"} <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      )}
      {result && (
        <div className="space-y-5" data-testid="hosp101-fa-result">
          <div className={`rounded-2xl border-2 p-6 text-center ${result.passed ? "border-emerald-400 bg-emerald-50" : "border-amber-400 bg-amber-50"}`}>
            <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-slate-600">Your Score</p>
            <p className={`mt-2 font-heading text-4xl ${result.passed ? "text-emerald-700" : "text-amber-700"}`}>
              {result.percent}% ({result.score}/{result.total})
            </p>
            <p className="mt-2 text-sm text-slate-700">
              {result.passed
                ? "You passed. The Final Course Reflection is now unlocked."
                : `You did not reach the 80% passing score. You may retake the assessment.`}
            </p>
            {!result.passed && (
              <button onClick={onRetake} data-testid="hosp101-fa-retake"
                      className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-lg border-2 border-amber-500 bg-white text-amber-800 text-sm font-medium hover:bg-amber-100">
                <RefreshCw className="w-4 h-4" /> Retake Assessment
              </button>
            )}
          </div>
          <div className="space-y-4">
            <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#4A6FA5]">Per-Question Rationale</p>
            {(result.review || []).map((r, i) => (
              <div key={r.id} className={`rounded-xl border p-4 ${r.is_correct ? "border-emerald-200 bg-white" : "border-amber-200 bg-white"}`} data-testid={`hosp101-fa-review-${r.id}`}>
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <p className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500">Question {i + 1} {"\u00b7"} {r.competency.split(" \u2014 ")[0]}</p>
                  <p className={`text-[11px] font-semibold ${r.is_correct ? "text-emerald-700" : "text-amber-700"}`}>
                    {r.is_correct ? "Correct" : "Incorrect"}
                  </p>
                </div>
                <p className="mt-2 text-sm text-slate-800 font-medium">{r.prompt}</p>
                <p className="mt-2 text-sm text-slate-700">
                  <span className="font-semibold">Your answer:</span> {(r.selected || "\u2014").toString().toUpperCase()}
                  <span className="mx-2 text-slate-300">|</span>
                  <span className="font-semibold">Correct:</span> {r.correct_key.toUpperCase()}
                </p>
                <p className="mt-2 text-sm text-slate-600 italic leading-relaxed">{r.correct_text}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ReflectionAck \u2014 completion acknowledgement (persisted; text NEVER stored).
// ---------------------------------------------------------------------------
function ReflectionAck({ screen, acked, onAck }) {
  return (
    <div className="space-y-5" data-testid="hosp101-refl-ack">
      {(screen.blocks || []).map((b, i) => <Block key={i} block={b} />)}
      <label className="flex items-start gap-3 rounded-xl border border-slate-300 bg-white px-4 py-3 cursor-pointer">
        <input
          type="checkbox"
          checked={acked}
          onChange={(e) => { if (e.target.checked && !acked) onAck(); }}
          disabled={acked}
          data-testid="hosp101-refl-ack-checkbox"
          className="mt-1 w-4 h-4 rounded border-slate-400 text-[#4A6FA5] focus:ring-[#4A6FA5]"
        />
        <span className="text-sm text-slate-800 leading-relaxed">
          <span className="font-semibold">{screen.ack_label || "I have completed the HOSP-101 Final Course Reflection."}</span>
          <span className="block text-[11px] text-slate-500 mt-1 italic">Your reflection responses above were never transmitted. Only this acknowledgement is stored, and only for course-progression purposes.</span>
        </span>
      </label>
      {acked && (
        <p className="text-xs font-semibold text-emerald-700">Acknowledged. Continue to Course Completion.</p>
      )}
    </div>
  );
}
